/*
 * Shop by Print — gallery family filter (with persistence) and per-print collection
 * behaviour (finish switcher, in-stock toggle, back link). No dependencies.
 */
(() => {
  if (window.__abPrintInit) return;
  window.__abPrintInit = true;

  const FAMILY_KEY = 'abPrintFamily';
  const LAST_KEY = 'abPrintLastTile';
  const RETURN_KEY = 'abPrintReturning';
  const FAMILIES = ['all', 'gingham', 'illustrated', 'houndstooth', 'texture'];

  const store = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable (private mode, blocked) — filter still works without it */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {
        /* ignore */
      }
    },
  };

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    if (root.dataset.abPrintReady) {
      if (root.abPrintApply) root.abPrintApply(store.get(FAMILY_KEY) || 'all', { updateUrl: false });
      return;
    }
    root.dataset.abPrintReady = 'true';

    const buttons = Array.from(root.querySelectorAll('[data-ab-print-family]'));
    const tiles = Array.from(root.querySelectorAll('.ab-print-tile'));
    const count = root.querySelector('[data-ab-print-count]');
    const one = root.dataset.labelOne || 'finish';
    const many = root.dataset.labelMany || 'finishes';

    const apply = (family, { updateUrl = true } = {}) => {
      if (!FAMILIES.includes(family)) family = 'all';
      let shown = 0;
      tiles.forEach((tile) => {
        const match = family === 'all' || tile.dataset.family === family;
        tile.hidden = !match;
        if (match) shown += 1;
      });
      buttons.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.abPrintFamily === family)));
      if (count) count.textContent = plural(shown, one, many);
      store.set(FAMILY_KEY, family);
      if (updateUrl && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        if (family === 'all') url.searchParams.delete('family');
        else url.searchParams.set('family', family);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    };

    root.abPrintApply = apply;
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => apply(btn.dataset.abPrintFamily));
    });

    root.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-ab-print-tile]');
      if (tile) store.set(LAST_KEY, tile.dataset.abPrintTile);
      const explore = event.target.closest('[data-ab-print-scroll]');
      if (explore) {
        const target = root.querySelector(explore.dataset.abPrintScroll);
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ block: 'start' });
          const first = target.querySelector('button, a');
          if (first) first.focus({ preventScroll: true });
        }
      }
    });

    const params = new URLSearchParams(window.location.search);
    const initial = params.get('family') || store.get(FAMILY_KEY) || 'all';
    apply(initial, { updateUrl: params.has('family') || initial !== 'all' });

    // Coming back from a print page via its back link: return to the tile they opened.
    if (store.get(RETURN_KEY)) {
      store.remove(RETURN_KEY);
      const last = store.get(LAST_KEY);
      const link = last && root.querySelector(`[data-ab-print-tile="${CSS.escape(last)}"]`);
      if (link && !link.closest('[hidden]')) {
        link.scrollIntoView({ block: 'center' });
        link.focus({ preventScroll: true });
      }
    }
  }

  /* ---------- Per-print collection ---------- */
  function initCollection(root) {
    if (root.dataset.abPrintReady) return;
    root.dataset.abPrintReady = 'true';

    const select = root.querySelector('[data-ab-print-switch]');
    if (select) {
      select.addEventListener('change', () => {
        if (select.value) window.location.href = select.value;
      });
    }

    root.querySelectorAll('[data-ab-print-back]').forEach((link) => {
      const family = store.get(FAMILY_KEY);
      if (family && family !== 'all') {
        try {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('family', family);
          link.href = url.toString();
        } catch (e) {
          /* keep the original link */
        }
      }
      link.addEventListener('click', () => store.set(RETURN_KEY, '1'));
    });

    const toggle = root.querySelector('[data-ab-print-stock]');
    const label = toggle && toggle.closest('.ab-print-stock');
    const cards = Array.from(root.querySelectorAll('.ab-print-card'));
    const count = root.querySelector('[data-ab-print-count]');
    const empty = root.querySelector('[data-ab-print-empty]');
    const one = root.dataset.labelOne || 'piece';
    const many = root.dataset.labelMany || 'pieces';

    if (toggle) {
      if (label) label.hidden = false;
      const apply = () => {
        let shown = 0;
        cards.forEach((card) => {
          const hide = toggle.checked && card.dataset.available !== 'true';
          card.hidden = hide;
          if (!hide) shown += 1;
        });
        if (count) count.textContent = plural(shown, one, many);
        if (empty) empty.hidden = shown !== 0;
      };
      toggle.addEventListener('change', apply);
      apply();
    }

    root.addEventListener('click', (event) => {
      const explore = event.target.closest('[data-ab-print-scroll]');
      if (!explore) return;
      const target = root.querySelector(explore.dataset.abPrintScroll);
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ block: 'start' });
      }
    });
  }

  const init = () => {
    document.querySelectorAll('[data-ab-print-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-ab-print-collection]').forEach(initCollection);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  // bfcache restores keep state; re-sync the filter in case sessionStorage changed.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-ab-print-gallery]').forEach(initGallery);
  });
})();
